import { createSlice } from '@reduxjs/toolkit';
import { catchError } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import driverService from '../services/driverService';
import { Driver } from './models/driver.model';
import { PagedList } from './models/paged-list.model';
import { SortModel } from './models/sort-model.model';
import { DriverDocument } from './models/document.model';
import userService from '../services/userService';
import emailUtils from '../services/emailUtils';

interface DriverState {
  selectedFilter: string;
  searchFilter: string;
  page: number;
  pageSize: number;
  sortModel: SortModel | null;
  loadingDriverList: boolean;
  loadedDriverList: boolean;
  driverList: PagedList<Driver>;
  onboardingDrivers: Driver[];

  loadingDriverListByProvider: boolean;
  loadedDriverListByProvider: boolean;
  driverListByProvider: PagedList<Driver>;

  loadingDriver: boolean;
  loadedDriver: boolean;
  savingDriver: boolean;
  savedDriver: boolean;
  savingError: string | null;
  driver: Driver | null;
  driverDocuments: DriverDocument[];
  uploadingDocument: boolean;
  uploadedDocument: boolean;
  uploadError: string | null;

  importingDrivers: boolean;
  importedDrivers: boolean;
  importDriversError: string | null;
  importDriversSuccess: string | null;

  driverSnackbarMessage: string | null;
  driverSnackbarError: string | null;
  userRolesID: [];

  validateEmail: boolean;
  validateUsername: boolean;
  validateFields: string[];
}

export const initialState: DriverState = {
  selectedFilter: ' ',
  searchFilter: '',
  page: 1,
  pageSize: 10,
  sortModel: null,
  loadingDriverList: false,
  loadedDriverList: false,
  driverList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  onboardingDrivers: [],

  loadingDriverListByProvider: false,
  loadedDriverListByProvider: false,
  driverListByProvider: {
    itemCount: 0,
    currentPage: 1,
    totalPages: 0,
    items: [],
  },

  loadingDriver: false,
  loadedDriver: false,
  savingDriver: false,
  savedDriver: false,
  savingError: null,
  driver: null,
  driverDocuments: [],
  uploadingDocument: false,
  uploadedDocument: false,
  uploadError: null,

  importingDrivers: false,
  importedDrivers: false,
  importDriversError: null,
  importDriversSuccess: null,

  driverSnackbarMessage: null,
  driverSnackbarError: null,
  userRolesID: [],
  validateEmail: false,
  validateUsername: false,
  validateFields: [],
};

export const slice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    setValidateEmail: (state: DriverState, action: { payload: boolean }) => {
      state.validateEmail = action.payload;
    },
    setValidateUsername: (state: DriverState, action: { payload: boolean }) => {
      state.validateUsername = action.payload;
    },
    setValidateFields: (state: DriverState, action: { payload: string }) => {
      const allowedPayload = ['email', 'username'];
      if (!allowedPayload.includes(action.payload)) {
        state.validateFields = [];
        return;
      }
      state.validateFields = [...state.validateFields, action.payload];
    },
    setSelectedFilter: (state: DriverState, action: { payload: string }) => {
      state.selectedFilter = action.payload;
      state.page = 1;
    },
    setSearchFilter: (state: DriverState, action: { payload: string }) => {
      state.searchFilter = action.payload;
      state.page = 1;
    },
    setPage: (state: DriverState, action: { payload: number }) => {
      state.page = action.payload;
    },
    setPageSize: (state: DriverState, action: { payload: number }) => {
      state.pageSize = action.payload;
    },
    setSortModel: (
      state: DriverState,
      action: { payload: SortModel | null },
    ) => {
      state.sortModel = action.payload;
    },
    setLoadingDriverList: (
      state: DriverState,
      action: { payload: boolean },
    ) => {
      state.loadingDriverList = action.payload;
    },
    setLoadedDriverList: (state: DriverState, action: { payload: boolean }) => {
      state.loadedDriverList = action.payload;
    },
    setDriverList: (
      state: DriverState,
      action: { payload: PagedList<Driver> },
    ) => {
      state.loadingDriverList = false;
      state.loadedDriverList = true;
      state.driverList = action.payload;
    },
    setOnboardingDriverList: (
      state: DriverState,
      action: { payload: PagedList<Driver> },
    ) => {
      state.loadingDriverList = false;
      state.loadedDriverList = true;
      state.onboardingDrivers = action.payload.items;
    },
    setLoadingDriver: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingDriver = action.payload;
    },
    setLoadedDriver: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedDriver = action.payload;
    },
    setSavingDriver: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingDriver = action.payload;
    },
    setSavedDriver: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedDriver = action.payload;
    },
    setSavingError: (
      state: DriverState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingError = action.payload;
    },
    resetSavingFlags: (state: DriverState, action: { type: string }) => {
      state.savingDriver = false;
      state.savedDriver = false;
      state.savingError = null;
    },
    setDriver: (
      state: DriverState,
      action: { type: string; payload: Driver | null },
    ) => {
      state.driver = action.payload;
    },
    setDriverListByProvider: (
      state: DriverState,
      action: { type: string; payload: PagedList<Driver> },
    ) => {
      state.loadingDriverListByProvider = !action.payload;
      state.loadedDriverListByProvider = !!action.payload;
      state.driverListByProvider = action.payload;
    },
    setUserRolesID: (
      state: DriverState,
      action: { type: string; payload: [] | any },
    ) => {
      state.userRolesID = action.payload;
    },
    addDriver: (
      state: DriverState,
      action: { type: string; payload: Driver | null },
    ) => {
      state.loadingDriver = false;
      state.loadedDriver = !!action.payload;
      state.driver = action.payload;
      if (null != state.driver) {
        // The list of drivers have email addresses as an array of string, not full EmailAddress model
        // So, we update EmailAddresses to have the array of email string
        const newDriver = {
          ...state.driver,
          emailAddresses: state.driver.emailAddresses?.map(
            (email) => email.EmailAddress,
          ),
        };

        // In the short term we allow adding a driver without a cognito account
        // If they don't have a cognito account (no UserAccountID), then we
        // add the driver to the non onboarded users.
        if (state.driver.userAccountDriver?.userAccountID) {
          state.driverList = {
            ...state.driverList,
            itemCount: state.driverList.itemCount + 1,
            items: [newDriver, ...state.driverList.items],
          };
        } else {
          state.onboardingDrivers = [...state.onboardingDrivers, newDriver];
        }
      }
    },
    updateDriver: (
      state: DriverState,
      action: { type: string; payload: Driver | null },
    ) => {
      state.loadingDriver = false;
      state.loadedDriver = !!action.payload;
      state.driver = action.payload;
      if (null != state.driver) {
        const drivers = [...state.driverList.items];
        const idx = drivers.findIndex(
          (v) => v.driverID === state.driver?.driverID,
        );
        // The list of drivers have email addresses as an array of string, not full EmailAddress model
        // So, we update EmailAddresses to have the array of email string
        const updatedDriver = {
          ...state.driver,
          emailAddresses: state.driver.emailAddresses?.map(
            (email) => email.emailAddress,
          ),
        };
        drivers.splice(idx, 1, updatedDriver);
        state.driverList = {
          ...state.driverList,
          items: drivers,
        };
      }
    },
    deleteDriver: (
      state: DriverState,
      action: { type: string; payload: number },
    ) => {
      const idx = state.onboardingDrivers.findIndex(
        (item) => item.driverID === action.payload,
      );
      if (idx >= 0) {
        state.onboardingDrivers.splice(idx, 1);
        state.onboardingDrivers = [...state.onboardingDrivers];
      }
    },
    setDriverDocuments: (
      state: DriverState,
      action: { type: string; payload: DriverDocument[] },
    ) => {
      state.driverDocuments = action.payload;
    },
    setUploadingDocument: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.uploadingDocument = action.payload;
    },
    setUploadedDocument: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.uploadedDocument = action.payload;
    },
    setUploadError: (
      state: DriverState,
      action: { type: string; payload: string | null },
    ) => {
      state.uploadError = action.payload;
    },
    addDriverDocument: (
      state: DriverState,
      action: { type: string; payload: DriverDocument },
    ) => {
      state.driverDocuments = [...state.driverDocuments, action.payload];
    },
    updateDriverDocument: (
      state: DriverState,
      action: { type: string; payload: DriverDocument },
    ) => {
      const docs = [...state.driverDocuments];
      const idx = docs.findIndex(
        (d) => d.driverDocumentID === action.payload.driverDocumentID,
      );
      if (idx >= 0) {
        docs.splice(idx, 1, action.payload);
        state.driverDocuments = docs;
      }
    },
    deleteDriverDocument: (
      state: DriverState,
      action: { type: string; payload: number },
    ) => {
      const docs = [...state.driverDocuments];
      const idx = docs.findIndex((d) => d.driverDocumentID === action.payload);
      if (idx >= 0) {
        docs.splice(idx, 1);
        state.driverDocuments = docs;
      }
    },

    setImportingDrivers: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.importingDrivers = action.payload;
    },
    setImportedDrivers: (
      state: DriverState,
      action: { type: string; payload: boolean },
    ) => {
      state.importedDrivers = action.payload;
    },
    setImportDriversError: (
      state: DriverState,
      action: { type: string; payload: string | null },
    ) => {
      state.importDriversError = action.payload;
    },
    setImportDriversSuccess: (
      state: DriverState,
      action: { type: string; payload: string | null },
    ) => {
      state.importDriversSuccess = action.payload;
    },
    resetImportFlags: (state: DriverState, action: { type: string }) => {
      state.importingDrivers = false;
      state.importedDrivers = false;
      state.importDriversError = null;
      state.importDriversSuccess = null;
    },
    setDriverSnackbarMessage: (
      state: DriverState,
      action: { type: string; payload: string | null },
    ) => {
      state.driverSnackbarMessage = action.payload;
    },
    setDriverSnackbarError: (
      state: DriverState,
      action: { type: string; payload: string | null },
    ) => {
      state.driverSnackbarError = action.payload;
    },
  },
});
export const { setValidateEmail } = slice.actions;
export const { setValidateUsername } = slice.actions;
export const { setValidateFields } = slice.actions;

export const { setSelectedFilter } = slice.actions;
export const { setSearchFilter } = slice.actions;
export const { setPage } = slice.actions;
export const { setPageSize } = slice.actions;
export const { setSortModel } = slice.actions;
export const { setLoadingDriverList } = slice.actions;
export const { setLoadedDriverList } = slice.actions;
export const { setDriverList } = slice.actions;
export const { setOnboardingDriverList } = slice.actions;

export const { setLoadingDriver } = slice.actions;
export const { setLoadedDriver } = slice.actions;
export const { setSavingDriver } = slice.actions;
export const { setSavedDriver } = slice.actions;
export const { setSavingError } = slice.actions;
export const { resetSavingFlags } = slice.actions;
export const { setDriver } = slice.actions;
export const { setDriverListByProvider } = slice.actions;
export const { setUserRolesID } = slice.actions;
export const { addDriver } = slice.actions;
export const { updateDriver } = slice.actions;
export const { deleteDriver } = slice.actions;
export const { setDriverDocuments } = slice.actions;
export const { addDriverDocument } = slice.actions;
export const { updateDriverDocument } = slice.actions;
export const { deleteDriverDocument } = slice.actions;
export const { setUploadingDocument } = slice.actions;
export const { setUploadedDocument } = slice.actions;
export const { setUploadError } = slice.actions;
export const { setImportingDrivers } = slice.actions;
export const { setImportedDrivers } = slice.actions;
export const { setImportDriversError } = slice.actions;
export const { setImportDriversSuccess } = slice.actions;
export const { resetImportFlags } = slice.actions;
export const { setDriverSnackbarMessage } = slice.actions;
export const { setDriverSnackbarError } = slice.actions;

export const fetchDriverListAsync =
  (
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    onboarded: boolean,
    providerId?: number,
  ) =>
  (dispatch: Function) => {
    dispatch(setLoadingDriverList(true));
    dispatch(setLoadedDriverList(false));
    driverService
      .findDrivers(
        pageSize,
        page,
        filter,
        search,
        sortModel,
        onboarded,
        providerId,
      )
      .subscribe((driverList) => {
        onboarded
          ? dispatch(setDriverList(driverList))
          : dispatch(setOnboardingDriverList(driverList));
      });
  };

export const refetchDriverListAsync =
  (providerId: number | undefined) =>
  (dispatch: Function, getState: Function) => {
    const state = getState();
    const driverState = state.driver;
    const providerState = state.provider;
    const selectedFilter = providerId
      ? providerState.driverSelectedFilter
      : driverState.selectedFilter;
    const searchFilter = providerId
      ? providerState.driverSearchFilter
      : driverState.searchFilter;
    dispatch(
      fetchDriverListAsync(
        driverState.pageSize,
        driverState.page,
        selectedFilter,
        searchFilter,
        driverState.sortModel,
        true,
        providerId,
      ),
    );
  };

export const refetchUninvitedDriverListAsync =
  (providerId: number | undefined) =>
  (dispatch: Function, getState: Function) => {
    const state = getState();
    const driverState = state.driver;
    const providerState = state.provider;
    const selectedFilter = providerId
      ? providerState.driverSelectedFilter
      : driverState.selectedFilter;
    const searchFilter = providerId
      ? providerState.driverSearchFilter
      : driverState.searchFilter;
    dispatch(
      fetchDriverListAsync(
        driverState.pageSize,
        driverState.page,
        selectedFilter,
        searchFilter,
        driverState.sortModel,
        true,
        providerId,
      ),
    );
  };

export const getDriverAsync = (id: number) => (dispatch: Function) => {
  dispatch(setDriver(null));
  dispatch(setLoadingDriver(true));
  dispatch(setLoadedDriver(false));
  driverService.getDriver(id).subscribe({
    next: (driver) => {
      dispatch(setLoadedDriver(true));
      dispatch(setLoadingDriver(false));
      dispatch(setDriver(driver));
    },
    error: () => {
      dispatch(setLoadedDriver(false));
      dispatch(setLoadingDriver(false));
    },
  });
};

export const getDriverByProviderAsync =
  (providerId: number) => (dispatch: Function) => {
    driverService.getDriversByProvider(providerId).subscribe((drivers) => {
      dispatch(setDriverListByProvider(drivers));
    });
  };

export const getUserRolesAsync = () => (dispatch: Function) => {
  userService.getUserRoles(4).subscribe((roles) => {
    const d = roles.map((r) => r.userRoleID);
    dispatch(setUserRolesID(d));
  });
};

const handleSaveDriverSuccess = (
  dispatch: Function,
  driver: Driver,
  isNew: boolean,
) => {
  dispatch(isNew ? addDriver(driver) : updateDriver(driver));
  dispatch(setSavingDriver(false));
  dispatch(setSavedDriver(true));
};

const handleSaveDriverError = (dispatch: Function, error: any) => {
  dispatch(setSavingDriver(false));
  dispatch(setSavedDriver(false));
  // This should be localized from messageId
  dispatch(setSavingError(error?.message));
};

export const createDriverAsync = (driver: Driver) => (dispatch: Function) => {
  userService.getUserRoles(4).subscribe((roles) => {
    const email =
      (driver?.emailAddresses && driver?.emailAddresses[0]?.emailAddress) ?? '';
    const username = driver.preferredUserName ?? '';
    userService
      .validateEmailUsername(`username`, username)
      .pipe(
        catchError((err) => {
          //handleSaveDriverError(dispatch, err);
          dispatch(setValidateFields('username'));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((dta: any) => {
        userService
          .validateEmailUsername('email', email)
          .pipe(
            catchError((err) => {
              handleSaveDriverError(dispatch, err);
              return throwError(() => new Error(err.message));
            }),
          )
          .subscribe((uda: any) => {
            const d: number[] = [];
            roles.forEach((r) => {
              if (r.userRoleID) d.push(r.userRoleID);
            });
            driver.userRoleIDs = d;

            dispatch(setDriver(null));
            dispatch(setSavingDriver(true));
            dispatch(setSavedDriver(false));
            dispatch(setSavingError(null));
            driverService
              .createDriver(driver)
              .pipe(
                catchError((err) => {
                  handleSaveDriverError(dispatch, err);
                  return of(null);
                }),
              )
              .subscribe((createdDriver) => {
                if (createdDriver) {
                  handleSaveDriverSuccess(dispatch, createdDriver, true);
                }
              });
          });
      });
  });
};

export const resendMailForUninvitedDriverAsync =
  (driver: Driver, username: string, providerId?: number) =>
  (dispatch: Function) => {};

export const updateDriverAsync =
  (driver: Driver, validateEmail: boolean, driverUserID: Number | undefined) =>
  (dispatch: Function) => {
    if (validateEmail) {
      const email = emailUtils.getDriverEmail(driver.emailAddresses);
      userService
        .validateEmailUsername('email', email?.emailAddress)
        .pipe(
          catchError((err) => {
            handleSaveDriverError(dispatch, err);
            dispatch(setValidateEmail(false));

            return throwError(() => new Error(err.message));
          }),
        )
        .subscribe((d: any) => {
          dispatch(setSavingDriver(true));
          dispatch(setSavedDriver(false));
          dispatch(setSavingError(null));

          dispatch(setValidateFields('email'));
          dispatch(setValidateEmail(true));

          driverService
            .updateDriver(driver)
            .pipe(
              catchError((err) => {
                handleSaveDriverError(dispatch, err);
                return throwError(() => new Error(err.message));
              }),
            )
            .subscribe((updatedDriver) => {
              handleSaveDriverSuccess(dispatch, updatedDriver, false);
              if (driverUserID) {
                userService.getUserRoles(4).subscribe((roles) => {
                  const d = roles.map((r) => r.userRoleID);
                  const userPayload = {
                    userFirstName: driver.driverFirstName,
                    userLastName: driver.driverLastName,
                    userMiddleName: driver.driverMiddleName,
                    emailAddress: email.EmailAddress,
                    userRoleIDs: d,
                  };
                  userService.updateDriverUser(userPayload, driverUserID);
                });
              }
            });
        });
      return;
    }
    dispatch(setSavingDriver(true));
    dispatch(setSavedDriver(false));
    dispatch(setSavingError(null));

    driverService
      .updateDriver(driver)
      .pipe(
        catchError((err) => {
          handleSaveDriverError(dispatch, err);
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((updatedDriver) => {
        handleSaveDriverSuccess(dispatch, updatedDriver, false);
      });
  };

export const deleteDriverAsync =
  (providerId: number | undefined, driver: Driver, onboardedUser: boolean) =>
  (dispatch: Function) => {
    const driverId = driver.driverID ?? 0;
    const userId = driver?.userAccountDriver?.userAccountID ?? undefined;
    driverService
      .deleteDriver(driverId)
      .pipe(
        catchError((err) => {
          // This should be localized from messageId
          dispatch(setSavingError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        if (userId) {
          userService.deleteUser(userId).subscribe(() => {
            dispatch(refetchDriverListAsync(providerId));
          });
        }
        if (onboardedUser) {
          dispatch(refetchDriverListAsync(providerId));
        } else {
          dispatch(refetchUninvitedDriverListAsync(providerId));
          dispatch(deleteDriver(driverId));
        }
      });
  };

export const uploadDriverDocumentAsync =
  (driverID: number, file: any, document: DriverDocument) =>
  (dispatch: Function) => {
    dispatch(setUploadingDocument(true));
    dispatch(setUploadedDocument(false));
    dispatch(setUploadError(null));
    driverService.uploadDriverDocument(driverID, document, file).subscribe({
      next: (doc) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(true));
        dispatch(addDriverDocument(doc));
      },
      error: (err) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(false));
        dispatch(setUploadError('common.documentTooLarge'));
      },
    });
  };

export const updateDriverDocumentAsync =
  (providerID: number, document: DriverDocument) => (dispatch: Function) => {
    dispatch(setUploadingDocument(true));
    dispatch(setUploadedDocument(false));
    dispatch(setUploadError(null));
    driverService
      .updateDriverDocument(providerID, document)
      .pipe(
        catchError((err) => {
          dispatch(setUploadingDocument(false));
          dispatch(setUploadedDocument(false));
          dispatch(setUploadError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe((doc) => {
        dispatch(setUploadingDocument(false));
        dispatch(setUploadedDocument(true));
        dispatch(updateDriverDocument(doc));
      });
  };

export const deleteDriverDocumentAsync =
  (providerId: number, documentId: number) => (dispatch: Function) => {
    driverService
      .deleteDriverDocument(providerId, documentId)
      .pipe(
        catchError((err) => {
          dispatch(setUploadError(err.message));
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(deleteDriverDocument(documentId));
      });
  };

export const fetchDriverDocumentsAsync =
  (driverId: number) => (dispatch: Function) => {
    driverService
      .getDriverDocuments(driverId)
      // .pipe(
      //   catchError((err) => {
      //     // This should be localized from messageId
      //     dispatch(setSavingError(err.message));
      //     return throwError(() => new Error(err.message));
      //   }),
      // )
      .subscribe((documents) => {
        dispatch(setDriverDocuments(documents));
      });
  };

export const importDriversAsync =
  (providerId: number, file: File) => (dispatch: Function) => {
    dispatch(setImportingDrivers(true));
    dispatch(setImportedDrivers(false));
    dispatch(setImportDriversError(null));
    driverService
      .importDrivers(providerId, file)
      .pipe(
        catchError((err) => {
          dispatch(setImportingDrivers(false));
          // This should be localized from messageId
          dispatch(
            setImportDriversError(err.message || 'Driver import failed'),
          );
          return throwError(() => new Error(err.message));
        }),
      )
      .subscribe(() => {
        dispatch(setImportingDrivers(false));
        dispatch(setImportedDrivers(true));
        dispatch(setImportDriversSuccess('Driver import succeeded'));
      });
  };

export const sendActivationEmailsAsync =
  (driverIds: any[], error: boolean = false) =>
  (dispatch: Function) => {
    if (error) {
      dispatch(
        setDriverSnackbarError(
          'One or more of the selected Drivers lack a username - please edit or deselect these Drivers and try again',
        ),
      );
      return;
    }
    driverService.sendActivationEmails(driverIds).subscribe({
      next() {
        const msg = `User activation emails sent to ${driverIds.length} drivers`;
        dispatch(setDriverSnackbarMessage(msg));
      },
      error(err: any) {
        dispatch(
          setDriverSnackbarError('Failed to send user activation emails'),
        );
      },
    });
  };

export const getValidateFields = (state: { driver: DriverState }) =>
  state.driver.validateFields;
export const getValidateEmail = (state: { driver: DriverState }) =>
  state.driver.validateEmail;
export const getValidateUsername = (state: { driver: DriverState }) =>
  state.driver.validateUsername;

export const selectSelectedFilter = (state: { driver: DriverState }) =>
  state.driver.selectedFilter;
export const selectSearchFilter = (state: { driver: DriverState }) =>
  state.driver.searchFilter;
export const selectPage = (state: { driver: DriverState }) => state.driver.page;
export const selectPageSize = (state: { driver: DriverState }) =>
  state.driver.pageSize;
export const selectSortModel = (state: { driver: DriverState }) =>
  state.driver.sortModel;
export const selectLoadingDriverList = (state: { driver: DriverState }) =>
  state.driver.loadingDriverList;
export const selectLoadedDriverList = (state: { driver: DriverState }) =>
  state.driver.loadedDriverList;
export const selectDriverList = (state: { driver: DriverState }) =>
  state.driver.driverList;
export const selectLoadingDriverListByProvider = (state: {
  driver: DriverState;
}) => state.driver.loadingDriverListByProvider;
export const selectLoadedDriverListByProvider = (state: {
  driver: DriverState;
}) => state.driver.loadedDriverListByProvider;
export const selectDriverListByProvider = (state: { driver: DriverState }) =>
  state.driver.driverListByProvider;

export const selectUserRoleIds = (state: { driver: DriverState }) =>
  state.driver.userRolesID;
export const selectOnboardingDrivers = (state: { driver: DriverState }) =>
  state.driver.onboardingDrivers;

export const selectLoadingDriver = (state: { driver: DriverState }) =>
  state.driver.loadingDriver;
export const selectLoadedDriver = (state: { driver: DriverState }) =>
  state.driver.loadedDriver;
export const selectSavingDriver = (state: { driver: DriverState }) =>
  state.driver.savingDriver;
export const selectSavedDriver = (state: { driver: DriverState }) =>
  state.driver.savedDriver;
export const selectSavingError = (state: { driver: DriverState }) =>
  state.driver.savingError;
export const selectDriver = (state: { driver: DriverState }) =>
  state.driver.driver;
export const selectDriverDocuments = (state: { driver: DriverState }) =>
  state.driver.driverDocuments;
export const selectUploadingDocument = (state: { driver: DriverState }) =>
  state.driver.uploadingDocument;
export const selectUploadedDocument = (state: { driver: DriverState }) =>
  state.driver.uploadedDocument;
export const selectUploadError = (state: { driver: DriverState }) =>
  state.driver.uploadError;
export const selectImportingDrivers = (state: { driver: DriverState }) =>
  state.driver.importingDrivers;
export const selectImportedDrivers = (state: { driver: DriverState }) =>
  state.driver.importedDrivers;
export const selectImportDriversError = (state: { driver: DriverState }) =>
  state.driver.importDriversError;
export const selectImportDriversSuccess = (state: { driver: DriverState }) =>
  state.driver.importDriversSuccess;
export const selectDriverSnackbarMessage = (state: { driver: DriverState }) =>
  state.driver.driverSnackbarMessage;
export const selectDriverSnackbarError = (state: { driver: DriverState }) =>
  state.driver.driverSnackbarError;

export default slice.reducer;
